export let audioCtx: AudioContext | null = null;

export const initAudio = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
};

export const playTick = () => {
  if (!audioCtx) return;
  const time = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  
  // A cozy, warm, smooth pop sound (like a soft droplet or marimba)
  osc.type = 'sine';
  
  // Lower pitch with a gentle, shallow drop
  osc.frequency.setValueAtTime(600, time);
  osc.frequency.exponentialRampToValueAtTime(300, time + 0.05);

  // Soft attack (fade-in) and smooth, slow decay for a non-harsh tick
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.3, time + 0.01); // 10ms soft attack
  gain.gain.exponentialRampToValueAtTime(0.01, time + 0.15); // 150ms smooth tail
  
  osc.start(time);
  osc.stop(time + 0.2);
};

let demoInterval: NodeJS.Timeout | null = null;
let masterGain: GainNode | null = null;

export const startSpinLoop = () => {
  if (!audioCtx) return;
  
  if (demoInterval) {
    clearInterval(demoInterval);
  }

  if (!masterGain) {
    masterGain = audioCtx.createGain();
    masterGain.connect(audioCtx.destination);
  }

  // Reset master volume in case it was previously faded out
  masterGain.gain.cancelScheduledValues(audioCtx.currentTime);
  masterGain.gain.setValueAtTime(1, audioCtx.currentTime);

  // A pleasant, upbeat demo pentatonic melody (C Maj Pentatonic)
  const melody = [523.25, 587.33, 659.25, 783.99, 659.25, 880.00, 783.99, 587.33];
  let patternIdx = 0;
  
  demoInterval = setInterval(() => {
    if (!audioCtx) return;
    const time = audioCtx.currentTime;
    
    // Play the melody note (triangle wave for a cute flute/bell feel)
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(melody[patternIdx], time);
    
    // Soft volume envelope for the melody
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.04, time + 0.02); // Gentle attack
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.2); // Sweet decay
    
    osc.connect(gain);
    gain.connect(masterGain!); // Connect to master fader
    
    osc.start(time);
    osc.stop(time + 0.25);
    
    // Play an alternating bass pulse on the downbeats
    if (patternIdx % 4 === 0) {
      const bassOsc = audioCtx.createOscillator();
      const bassGain = audioCtx.createGain();
      
      bassOsc.type = 'sine';
      // Root note (C3 = 130.81Hz) on count 0, Fifth (G2 = 98.00Hz) on count 4
      bassOsc.frequency.setValueAtTime(patternIdx === 0 ? 130.81 : 98.00, time);
      
      bassGain.gain.setValueAtTime(0, time);
      bassGain.gain.linearRampToValueAtTime(0.06, time + 0.05);
      bassGain.gain.exponentialRampToValueAtTime(0.001, time + 0.4);
      
      bassOsc.connect(bassGain);
      bassGain.connect(masterGain!); // Connect to master fader
      
      bassOsc.start(time);
      bassOsc.stop(time + 0.5);
    }
    
    patternIdx = (patternIdx + 1) % melody.length;
  }, 130); // 130ms = pleasant upbeat tempo
};

export const stopSpinLoop = (immediate = false) => {
  if (!audioCtx || !masterGain || !demoInterval) {
    if (demoInterval) {
      clearInterval(demoInterval);
      demoInterval = null;
    }
    return;
  }

  const now = audioCtx.currentTime;
  masterGain.gain.cancelScheduledValues(now);

  if (immediate) {
    // Instantly silence it (e.g. for unmounting)
    masterGain.gain.setValueAtTime(0, now);
    clearInterval(demoInterval);
    demoInterval = null;
  } else {
    // Smoothly fade out the ongoing melody over 1.5 seconds
    masterGain.gain.setValueAtTime(1, now);
    masterGain.gain.linearRampToValueAtTime(0.001, now + 1.5);
    
    const intervalToClear = demoInterval;
    demoInterval = null;
    
    // Keep scheduling notes while fading, then stop after the fade finishes
    setTimeout(() => {
      clearInterval(intervalToClear);
    }, 1500);
  }
};



export const playWin = () => {
  if (!audioCtx) return;
  // An exciting arpeggio sequence for winning
  const notes = [440, 554.37, 659.25, 880]; // A Major: A4, C#5, E5, A5
  
  notes.forEach((freq, idx) => {
    const time = audioCtx!.currentTime + idx * 0.1;
    const osc = audioCtx!.createOscillator();
    const gain = audioCtx!.createGain();
    
    osc.connect(gain);
    gain.connect(audioCtx!.destination);
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);
    
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.3, time + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.6);
    
    osc.start(time);
    osc.stop(time + 0.6);
  });
};
